import { useCallback, useRef } from "react";
import type { GridReadyEvent, RowClickedEvent } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import type { RollApplication } from "../models/RollApplication";
import { useTableData } from "../hooks/useTableData";
import { useArrowRowNavigation } from "@/controls/ag-grid/useArrowRowNavigation";
import {
	LIST_HEADER_HEIGHT,
	LIST_ROW_HEIGHT,
	listColDef,
} from "@/controls/ag-grid/listGrid";
import {
	OneLineCellStyles,
	LIST_ROW_CLASS,
} from "@/controls/ag-grid/OneLineCells";

interface RollApplicationsTableProps {
	rollApplications: RollApplication[];
	selectedApplication: RollApplication | null;
	onRowSelected: (app: RollApplication | null) => void;
	onDelete: (app: RollApplication) => void;
}

export const RollApplicationsTable = ({
	rollApplications,
	selectedApplication,
	onRowSelected,
	onDelete,
}: RollApplicationsTableProps) => {
	const gridRef = useRef<AgGridReact<RollApplication>>(null);
	const { columnDefs } = useTableData({ onDelete });

	const onGridReady = useCallback(
		(_params: GridReadyEvent<RollApplication>) => {},
		[],
	);

	const onRowClicked = useCallback(
		(event: RowClickedEvent<RollApplication>) => {
			if (event.data) {
				onRowSelected(event.data);
			}
		},
		[onRowSelected],
	);

	const onCellKeyDown = useArrowRowNavigation<RollApplication>(onRowSelected);

	const getRowId = useCallback((params: { data: RollApplication }) => {
		return params.data.Id?.toString() ?? "";
	}, []);

	return (
		<>
			<OneLineCellStyles />
			<AgGridReact<RollApplication>
				ref={gridRef}
				rowData={rollApplications}
				columnDefs={columnDefs}
				defaultColDef={listColDef(globalDefaultColDef)}
				onGridReady={onGridReady}
				onRowClicked={onRowClicked}
				onCellKeyDown={onCellKeyDown}
				getRowId={getRowId}
				rowSelection="single"
				suppressRowClickSelection={false}
				suppressContextMenu
				alwaysShowVerticalScroll
				suppressColumnVirtualisation
				animateRows={false}
				enableBrowserTooltips
				rowClass={LIST_ROW_CLASS}
				rowHeight={LIST_ROW_HEIGHT}
				headerHeight={LIST_HEADER_HEIGHT}
				getRowStyle={(params) => {
					if (selectedApplication?.Id === params.data?.Id) {
						return { backgroundColor: "#e3f2fd", color: "inherit" };
					}
					if (params.data && !params.data.Active) {
						return { backgroundColor: "#f5f5f5", color: "#9e9e9e" };
					}
					return undefined;
				}}
			/>
		</>
	);
};
